import { getConsumerToken } from '../auth/session';
import { getAdminToken } from '../admin/api';

const nearrmeApiBaseUrl = (
  import.meta.env.VITE_NEARRME_API_URL ??
  import.meta.env.VITE_API_URL ??
  'https://app-nearrme-api-dev.azurewebsites.net'
).replace(/\/$/, '');

export type ContactMessageStatus = 'New' | 'InReview' | 'Resolved' | 'Archived';

export interface ContactMessageInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  sourcePage?: string;
}

export interface ContactMessage {
  contactMessageId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  status: ContactMessageStatus;
  adminNotes?: string | null;
  sourcePage?: string | null;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
}

async function nearrmeFetch<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${nearrmeApiBaseUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = await response.json();
      message = body.userMessage ?? body.message ?? body.title ?? message;
    } catch {
      // Keep the status text.
    }
    throw new Error(`HTTP ${response.status}: ${message || 'Request failed'}`);
  }

  return response.json() as Promise<T>;
}

export function submitContactMessage(input: ContactMessageInput): Promise<ContactMessage> {
  return nearrmeFetch<ContactMessage>(
    '/api/v1/contact-messages',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
    getConsumerToken()
  );
}

export function listContactMessages(status?: ContactMessageStatus): Promise<ContactMessage[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const query = params.toString();
  return nearrmeFetch<ContactMessage[]>(
    `/api/v1/contact-messages${query ? `?${query}` : ''}`,
    {},
    getAdminToken()
  );
}

export function updateContactMessageStatus(
  id: number,
  status: ContactMessageStatus,
  adminNotes?: string
): Promise<ContactMessage> {
  return nearrmeFetch<ContactMessage>(
    `/api/v1/contact-messages/${id}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status, adminNotes }),
    },
    getAdminToken()
  );
}
